<template>
    <Paper>
        <div v-if="isLoading || loading()">
            <CompanyModuleSkeleton />
        </div>
        <div v-else>
            <router-link :to="newTab" style="text-decoration:none;">
                <div class="image-wrapper image-center logo-clickable py-4">
                    <img class="image-center image" :src="data().logo" />
                </div>
            </router-link>
            <Divider />
            <div class="p-3">
                <router-link :to="newTab" style="text-decoration:none;">
                    <div class="d-flex justify-content-center align-items">
                        <div>
                            <h6 class="headline bold">{{ data().name }}</h6>
                            <h6 class="subtitle">{{ data().country }}</h6>
                        </div>
                        <div>
                            <!-- <h6 class="headline text-right">
                            {{ data().followerCount }}
                        </h6>
                        <h6 class="subtitle text-right">followers</h6> -->
                        </div>
                    </div>
                </router-link>
                <div v-if="showFavorite && hasLogin" class="mt-4">
                    <BaseButton
                        class="d-flex align-items-center justify-content-center"
                        :class="{
                            'w-100': true,
                            disabled: this.isLoadingFollow,
                        }"
                        @click="toggleFavorite"
                        variant="secondary"
                    >
                        <Spinner v-if="isLoadingFollow" size="sm" />
                        <BaseIcon v-else-if="!jobAdFavorite" name="heart" />
                        {{ jobAdFavorite ? unFollowText : followText }}
                    </BaseButton>
                </div>
            </div>
            <slot />
        </div>
    </Paper>
</template>

<script>
import CompanyModuleSkeleton from '~/components/skeletons/CompanyModuleSkeleton';
import MessageService from '~/services/MessageService';
import { HAS_LOGIN } from '~/router';

export default {
    components: { CompanyModuleSkeleton },
    props: {
        id: {
            // Company id
            type: String,
            required: false,
        },
        showFavorite: {
            // Decides if the Favorite-Position button should appear or not
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        jobAdId: {
            type: [String, Number],
            required: false,
        },
    },
    data: () => ({
        jobAdFavorite: null,
        isLoadingFollow: false, // Indicating if a (un)follow progress is under progress
        hasLogin: HAS_LOGIN,
    }),
    computed: {
        company() {
            return this.$interfaces.company.id(this.id).get({ reactive: true });
        },
        // followedCompanies() {
        //     return this.$interfaces.companyFollow.get({ reactive: true });
        // },
        userData() {
            return this.$interfaces.user.me.get({ reactive: true });
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
        followText() {
            return this.jobAdId
                ? this.$t('components.smart.companyModule.followJob')
                : this.$t('components.smart.companyModule.followCompany');
        },
        unFollowText() {
            return this.jobAdId
                ? this.$t('components.smart.companyModule.unFollowJob')
                : this.$t('components.smart.companyModule.unFollowCompany');
        },
        newTab() {
            const url = this.$URLS.findcompanies.concat('/', this.id);
            return url;
        },
    },
    methods: {
        data() {
            return this.company.data || {};
        },
        loading() {
            return this.company.loading;
        },
        goToCompany() {
            this.$router.push(this.$URLS.findcompanies.concat('/', this.id));
        },
        redir() {
            this.$interfaces.logInModule.toggleOpen();
        },
        toggleFavorite() {
            // Check if is authenticated
            if (!this.isAuthenticated) {
                this.$interfaces.logInModule.requireLogIn(this.$route.path);
                return;
            }

            this.isLoadingFollow = true;
            let handlerPromise = null;
            if (this.jobAdFavorite) {
                // Unfavorite jobAd
                handlerPromise = this.$interfaces.candidate.favorite
                    .id(this.jobAdFavorite.id)
                    .delete({ body: {} })
                    .then(() => {
                        this.jobAdFavorite = null;
                        MessageService.showUnfavoritedMessage(); // Show message notification
                        // Make sure to force update /favorite on visit
                        this.$interfaces.favoriteModule.reset();
                    });
            } else {
                // Favorite jobAd
                handlerPromise = this.$interfaces.candidate.favorite
                    .post({
                        body: { jobAdId: Number(this.jobAdId) },
                    })
                    .then(data => {
                        if (data && data.id) {
                            this.jobAdFavorite = data;
                        }
                        MessageService.showFavoritedMessage(); // Show message notification
                        // Make sure to force update /favorite on visit
                        this.$interfaces.favoriteModule.reset();
                    });
            }

            handlerPromise
                .catch(() => {
                    MessageService.error(
                        this.$t('components.smart.companyModule.favoriteErrorTitle'),
                        this.$t('components.smart.companyModule.favoriteErrorMessage')
                    );
                })
                .finally(() => (this.isLoadingFollow = false));
        },
        fetchJobFavoriteData() {
            // Can not get fetch favorite status without a jobAdId
            if (!this.jobAdId || !this.showFavorite) {
                return;
            }

            // Fetch favorite-status by id
            this.isLoadingFollow = true;
            this.$interfaces.candidate.favorite
                .id(this.jobAdId)
                .get({
                    isJobAdId: true,
                    excludeJob: true,
                    forceUpdate: true,
                })
                .then(data => {
                    if (data && data.data && data.data.id) {
                        this.jobAdFavorite = data.data;
                    }
                })
                .catch(console.log)
                .finally(() => (this.isLoadingFollow = false));
        },
    },
    mounted() {
        if (this.isAuthenticated) {
            this.fetchJobFavoriteData();
        }
    },
    destroyed() {
        this.jobAdFavorite = null;
    },
    watch: {
        jobAdId(newId, oldId) {
            // Only fetch job-favorite status if it is not done before
            if (newId !== oldId && this.showFavorite && this.isAuthenticated) {
                this.fetchJobFavoriteData();
            }
        },
    },
};
</script>

<style scoped>
.logo-clickable {
    cursor: pointer;
}

.image-wrapper {
    height: 175px;
    max-width: 200px;
    max-height: 200px;
    overflow: hidden;
}
.image {
    width: 100%;
    height: 100%;
    object-fit: scale-down;
}
</style>
